import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { calculateDropdownPosition, type DropdownPosition } from "./KBScopeSelector.position";

export type KBScope = "general" | "myStudies" | "personal";

interface KBScopeDef {
  key: KBScope;
  label: string;
  icon: string;
}

/** General Docs 的可选项。以 ID 作为选中标识——分类改名不应让已选中的项失效 */
export interface SourceTypeOption {
  id: string;
  name: string;
}

export interface StudyOption {
  id: string;
  code: string;
  name: string;
  /** 该 Study 下当前用户获授权的具体 SourcePathStudy.ID。 */
  studySourceIds: string[];
}

interface KBScopeSelectorProps {
  value: KBScope[];
  onChange: (scopes: KBScope[]) => void;
  size?: "sm" | "md";
  /** 当前用户可见的胶囊，由后端 AskScope 裁定；未列出的不渲染 */
  visibleScopes?: KBScope[];
  sourceTypes?: SourceTypeOption[];
  /** 选中的分类 ID；空数组表示「全部类型」，null 表示全不选 */
  selectedTypes?: string[] | null;
  onTypesChange?: (typeIds: string[] | null) => void;
  availableStudies?: StudyOption[];
  /** 选中的 Study.ID；空数组表示「全部 Study」，null 表示全不选 */
  selectedStudies?: string[] | null;
  onStudiesChange?: (studyIds: string[] | null) => void;
}

const DEFAULT_DROPDOWN_POSITION: DropdownPosition = {
  placement: "bottom",
  maxHeight: Number.POSITIVE_INFINITY,
};

function useDropdownPosition(
  open: boolean,
  anchorRef: React.RefObject<HTMLDivElement | null>,
  dropdownRef: React.RefObject<HTMLDivElement | null>,
) {
  const [position, setPosition] = useState<DropdownPosition>(DEFAULT_DROPDOWN_POSITION);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    const dropdown = dropdownRef.current;
    if (!anchor || !dropdown) return;

    const dropdownRect = dropdown.getBoundingClientRect();
    const naturalHeight = Math.max(dropdown.scrollHeight, dropdownRect.height);
    setPosition(
      calculateDropdownPosition(
        anchor.getBoundingClientRect(),
        { height: naturalHeight },
        { height: window.innerHeight },
      ),
    );
  }, [anchorRef, dropdownRef]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();

    window.addEventListener("resize", updatePosition);
    document.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      document.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, updatePosition]);

  return position;
}

export default function KBScopeSelector({
  value,
  onChange,
  size = "sm",
  visibleScopes,
  sourceTypes = [],
  selectedTypes = [],
  onTypesChange,
  availableStudies = [],
  selectedStudies = [],
  onStudiesChange,
}: KBScopeSelectorProps) {
  const { t } = useTranslation();

  const generalDocsLabel = useMemo(() => {
    if (selectedTypes?.length !== 1) return t("opsmate.scopeSelector.generalDocs");

    const selectedType = sourceTypes.find((sourceType) => sourceType.id === selectedTypes[0]);
    return selectedType?.name.trim() || t("opsmate.scopeSelector.generalDocs");
  }, [selectedTypes, sourceTypes, t]);

  const scopeDefs = useMemo<KBScopeDef[]>(() => {
    const all: KBScopeDef[] = [
      { key: "general", label: generalDocsLabel, icon: "ri-article-line" },
      {
        key: "myStudies",
        label: t("opsmate.scopeSelector.studySource"),
        icon: "ri-microscope-line",
      },
      {
        key: "personal",
        label: t("opsmate.scopeSelector.personalDocs"),
        icon: "ri-folder-user-line",
      },
    ];
    // visibleScopes 未传时保持全部可见（供不做权限裁剪的调用方使用）
    return visibleScopes ? all.filter((scope) => visibleScopes.includes(scope.key)) : all;
  }, [generalDocsLabel, t, visibleScopes]);

  const pillHeight = size === "sm" ? 24 : 28;
  const textSize = size === "sm" ? 11 : 12;
  const iconSize = size === "sm" ? 10 : 12;

  const [typesOpen, setTypesOpen] = useState(false);
  const [studiesOpen, setStudiesOpen] = useState(false);
  const typesRef = useRef<HTMLDivElement>(null);
  const studiesRef = useRef<HTMLDivElement>(null);
  const typesDropdownRef = useRef<HTMLDivElement>(null);
  const studiesDropdownRef = useRef<HTMLDivElement>(null);
  const typesPosition = useDropdownPosition(typesOpen, typesRef, typesDropdownRef);
  const studiesPosition = useDropdownPosition(studiesOpen, studiesRef, studiesDropdownRef);

  useEffect(() => {
    if (!typesOpen) return;
    const handler = (e: MouseEvent) => {
      if (typesRef.current && !typesRef.current.contains(e.target as Node)) {
        setTypesOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [typesOpen]);

  useEffect(() => {
    if (!studiesOpen) return;
    const handler = (e: MouseEvent) => {
      if (studiesRef.current && !studiesRef.current.contains(e.target as Node)) {
        setStudiesOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [studiesOpen]);

  const allTypesSelected = selectedTypes !== null && selectedTypes.length === 0;
  const allStudiesSelected = selectedStudies !== null && selectedStudies.length === 0;

  const getScopeCount = (scopeKey: KBScope): number | null => {
    if (scopeKey === "general") {
      if (selectedTypes === null) return 0;
      return selectedTypes.length > 0 ? selectedTypes.length : sourceTypes.length;
    }
    if (scopeKey === "myStudies") {
      if (selectedStudies === null) return 0;
      return selectedStudies.length > 0 ? selectedStudies.length : availableStudies.length;
    }
    return null;
  };

  const handleToggleScope = (scopeKey: KBScope) => {
    const active = value.includes(scopeKey);
    if (active) {
      if (scopeKey === "general") setTypesOpen(false);
      if (scopeKey === "myStudies") setStudiesOpen(false);
    } else if (scopeKey === "general" && selectedTypes === null) {
      onTypesChange?.([]);
    } else if (scopeKey === "myStudies" && selectedStudies === null) {
      onStudiesChange?.([]);
    }
    const next = active ? value.filter((scope) => scope !== scopeKey) : [...value, scopeKey];
    onChange(next);
  };

  const handleTypesChange = (typeIds: string[] | null) => {
    onTypesChange?.(typeIds);
    if (typeIds === null && value.includes("general")) {
      onChange(value.filter((scope) => scope !== "general"));
    } else if (typeIds !== null && !value.includes("general")) {
      onChange([...value, "general"]);
    }
  };

  const handleStudiesChange = (studyIds: string[] | null) => {
    onStudiesChange?.(studyIds);
    if (studyIds === null && value.includes("myStudies")) {
      onChange(value.filter((scope) => scope !== "myStudies"));
    } else if (studyIds !== null && !value.includes("myStudies")) {
      onChange([...value, "myStudies"]);
    }
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      {scopeDefs.map((scope) => {
        const active = value.includes(scope.key);
        const hasFilter =
          (scope.key === "general" && sourceTypes.length > 0) ||
          (scope.key === "myStudies" && availableStudies.length > 0);
        const showDropdown = active && hasFilter;
        const isOpen = scope.key === "general" ? typesOpen : studiesOpen;
        const setOpen = scope.key === "general" ? setTypesOpen : setStudiesOpen;
        const containerRef =
          scope.key === "general" ? typesRef : scope.key === "myStudies" ? studiesRef : null;

        return (
          <div
            key={scope.key}
            ref={containerRef}
            style={{ position: "relative", display: "inline-flex" }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                height: pillHeight,
                borderRadius: 999,
                border: active ? "1px solid #830051" : "1px solid #d1d5db",
                background: active ? "#830051" : "#fff",
                transition: "all 0.2s ease",
              }}
            >
              <button
                type="button"
                onClick={() => handleToggleScope(scope.key)}
                style={{
                  height: "100%",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  color: active ? "#fff" : "#6b7280",
                  fontSize: textSize,
                  padding: showDropdown ? "0 8px 0 10px" : "0 10px",
                  borderRadius: showDropdown ? "999px 0 0 999px" : 999,
                }}
              >
                <i className={scope.icon} style={{ fontSize: iconSize }} />
                <span style={{ whiteSpace: "nowrap" }}>{scope.label}</span>
                {getScopeCount(scope.key) !== null ? (
                  <span
                    style={{
                      minWidth: 18,
                      height: 18,
                      borderRadius: 999,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 10,
                      fontWeight: 700,
                      background: active ? "rgba(255,255,255,0.18)" : "#f3f4f6",
                      color: active ? "#fff" : "#6b7280",
                      padding: "0 4px",
                    }}
                  >
                    {getScopeCount(scope.key)}
                  </span>
                ) : null}
              </button>
              {showDropdown ? (
                <button
                  type="button"
                  onClick={() => setOpen((open) => !open)}
                  style={{
                    height: "100%",
                    border: "none",
                    borderLeft: active ? "1px solid rgba(255,255,255,0.3)" : "1px solid #e5e7eb",
                    borderRadius: "0 999px 999px 0",
                    background: "transparent",
                    color: active ? "#fff" : "#6b7280",
                    cursor: "pointer",
                    width: 22,
                  }}
                >
                  <i
                    className="ri-arrow-down-s-line"
                    style={{
                      fontSize: iconSize,
                      display: "inline-block",
                      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                  />
                </button>
              ) : null}
            </div>

            {scope.key === "general" && typesOpen ? (
              <div
                ref={typesDropdownRef}
                style={{
                  position: "absolute",
                  left: 0,
                  ...(typesPosition.placement === "top"
                    ? { bottom: "100%", marginBottom: 6 }
                    : { top: "100%", marginTop: 6 }),
                  zIndex: 40,
                  backgroundColor: "#fff",
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
                  minWidth: 180,
                  maxHeight: Number.isFinite(typesPosition.maxHeight)
                    ? typesPosition.maxHeight
                    : undefined,
                  overflowY: "auto",
                  padding: "4px 0",
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 12px",
                    cursor: "pointer",
                    fontSize: 11,
                    color: "#374151",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={allTypesSelected}
                    style={{ accentColor: "#830051" }}
                    onChange={(event) => handleTypesChange(event.target.checked ? [] : null)}
                  />
                  <span style={{ fontWeight: allTypesSelected ? 600 : 400 }}>
                    {t("opsmate.scopeSelector.allTypes")}
                  </span>
                </label>
                <div style={{ margin: "2px 8px", borderTop: "1px solid #f3f4f6" }} />
                {sourceTypes.map((sourceType) => {
                  const checked =
                    allTypesSelected || (selectedTypes?.includes(sourceType.id) ?? false);
                  return (
                    <label
                      key={sourceType.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "6px 12px",
                        cursor: "pointer",
                        fontSize: 11,
                        color: "#374151",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        style={{ accentColor: "#830051" }}
                        onChange={() => {
                          const current = allTypesSelected
                            ? sourceTypes.map((item) => item.id)
                            : (selectedTypes ?? []);
                          const next = checked
                            ? current.filter((item) => item !== sourceType.id)
                            : [...current, sourceType.id];
                          // 空数组专门表示顶部的「All Types」选项；
                          // 用户逐项勾选时保留完整 ID，避免选中最后一项后切换成 All Types。
                          handleTypesChange(next.length > 0 ? next : null);
                        }}
                      />
                      <span style={{ fontWeight: checked ? 600 : 400 }}>{sourceType.name}</span>
                    </label>
                  );
                })}
              </div>
            ) : null}

            {scope.key === "myStudies" && studiesOpen ? (
              <div
                ref={studiesDropdownRef}
                style={{
                  position: "absolute",
                  left: 0,
                  ...(studiesPosition.placement === "top"
                    ? { bottom: "100%", marginBottom: 6 }
                    : { top: "100%", marginTop: 6 }),
                  zIndex: 40,
                  backgroundColor: "#fff",
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
                  minWidth: 220,
                  maxWidth: 280,
                  maxHeight: Number.isFinite(studiesPosition.maxHeight)
                    ? studiesPosition.maxHeight
                    : undefined,
                  overflowY: "auto",
                  padding: "4px 0",
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 12px",
                    cursor: "pointer",
                    fontSize: 11,
                    color: "#374151",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={allStudiesSelected}
                    style={{ accentColor: "#830051" }}
                    onChange={(event) => handleStudiesChange(event.target.checked ? [] : null)}
                  />
                  <span style={{ fontWeight: allStudiesSelected ? 600 : 400 }}>
                    {t("opsmate.scopeSelector.allStudies")}
                  </span>
                </label>
                <div style={{ margin: "2px 8px", borderTop: "1px solid #f3f4f6" }} />
                {availableStudies.map((study) => {
                  const checked =
                    allStudiesSelected || (selectedStudies?.includes(study.id) ?? false);
                  return (
                    <label
                      key={study.id}
                      title={study.name}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "6px 12px",
                        cursor: "pointer",
                        fontSize: 11,
                        color: "#374151",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        style={{ accentColor: "#830051" }}
                        onChange={() => {
                          const current = allStudiesSelected
                            ? availableStudies.map((item) => item.id)
                            : (selectedStudies ?? []);
                          const next = checked
                            ? current.filter((item) => item !== study.id)
                            : [...current, study.id];
                          // 空数组专门表示顶部的「All Studies」选项；逐项勾满时仍保留
                          // 完整 Study.ID，不能和主动点击 All 的语义混用。
                          handleStudiesChange(next.length > 0 ? next : null);
                        }}
                      />
                      <span style={{ fontWeight: checked ? 600 : 400 }}>
                        {study.code} - {study.name}
                      </span>
                    </label>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
